// ----------------------------------------------------------------------- externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- internos
import { useAuth } from "../auth/AuthContext";
import { MARCA_LARGA } from "../marca";
import { SECCIONES, puedeVer } from "../components/layout/secciones";
import { guiaQueMeToca } from "../services/guiaDelBot";
import Icono from "../components/Icono";
import "./css/Ayuda.css";

/* Cómo se usa esto. La pantalla que se le manda a alguien el primer día.
 *
 * POR QUÉ VIVE ACÁ Y NO EN EL MAIL DE BIENVENIDA. Un mail se lee una vez y se
 * archiva; la pregunta "¿dónde estaba lo de la clave?" aparece a la semana,
 * cuando ese mail ya no se encuentra. Acá está siempre, en el mismo lugar
 * donde surge la duda, y el mail sólo tiene que traer el enlace.
 *
 * LA LISTA DE SECCIONES SE ARMA SOLA, desde `SECCIONES` — la misma que dibuja
 * el menú. Escribirla a mano garantizaría que el día que se agregue una
 * pantalla el tutorial quede mintiendo, y un tutorial desactualizado es peor
 * que ninguno: manda a buscar cosas que no están.
 *
 * Y MUESTRA LO QUE ESA PERSONA VE, no el catálogo completo. Alguien con tres
 * permisos que abre una lista de veinte pantallas se queda con la idea de que
 * el sistema no le anda — y no es eso, es que no le tocan. Las que no tiene se
 * cuentan aparte: cuántas son y que se piden, sin nombrar a quién.
 *
 * SE LEE SIN CUENTA, y no es un descuido: éste es el enlace que va en el mail
 * con la clave provisoria, así que quien lo abre todavía no entró nunca. Sin
 * sesión no hay "lo tuyo" que mostrar, y entonces la lista es el catálogo
 * entero — que es la respuesta correcta a la pregunta que trae esa persona:
 * qué hay adentro. El marco lo elige `MarcoAbierto`.
 */

const Ayuda = () => {
  const { fullUser } = useAuth();
  /* LOS PERMISOS VIENEN DEL ARMAZÓN, y antes se pedían acá.
   *
   * El comentario que había decía que atarlos a un prop obligaría al Layout a
   * pasárselos a todas las rutas — y para cuando se escribió eso ya lo hacía,
   * por el contexto del `Outlet`. O sea que esta pantalla salía a buscar por
   * segunda vez algo que ya estaba en memoria.
   *
   * Lo que costaba no era la consulta: **son los permisos de la sesión REAL, y
   * por eso «Ver como» no llegaba hasta acá.** Previsualizar a alguien con
   * tres pantallas y que «Cómo se usa» siguiera listando las veinte es la
   * vista previa diciendo lo contrario de lo que dice el menú de al lado.
   *
   * SIN SESIÓN NO HAY CONTEXTO —esta pantalla también se abre desde el mail de
   * la clave, bajo `MarcoAbierto`— y ahí `permisos` queda en `null`, que ya
   * significa "todavía no se sabe" y muestra el catálogo entero. Es
   * exactamente la respuesta correcta para quien todavía no entró nunca. */
  const { permisos = null } = useOutletContext() || {};

  /* LA GUÍA DEL BOT QUE LE TOCA A ESTA PERSONA.
   *
   * Igna: «¿podemos hacer que dependiendo la persona que entra acá y los roles
   * que tiene lo mande a ver el howto que le corresponde?».
   *
   * La decide el bot y no esta pantalla — el detalle está en `guiaDelBot`—:
   * los permisos del hub dicen qué pantallas ve alguien, y las guías dicen qué
   * le contesta el BOT, que va por los roles de la planilla. No es lo mismo, y
   * deducirlo acá mandaría a leer comandos que el bot rebota.
   *
   * `undefined` es «todavía no se sabe» y `null` es «no se pudo preguntar»: en
   * los dos casos queda el texto de siempre, sin botón. Un botón que aparece
   * medio segundo tarde es mejor que uno que lleva a la guía equivocada. */
  const [guia, setGuia] = useState(undefined);
  useEffect(() => {
    let vigente = true;
    guiaQueMeToca()
      .then((g) => vigente && setGuia(g))
      .catch(() => vigente && setGuia(null));
    return () => {
      vigente = false;
    };
  }, []);

  const mias = useMemo(() => {
    const m = [];
    SECCIONES.forEach((g) => {
      (g.items || []).forEach((i) => {
        if (i.donde === "pronto") return; // todavía no existe: no se promete
        // Lo que el rail no dibuja (`oculto`, como el RTO) tampoco va acá: la
        // lista dice «a la izquierda está el menú con todo lo que podés
        // abrir», y nombrar algo que el menú no tiene manda a buscarlo.
        if (i.oculto) return;
        if (puedeVer(i, permisos)) m.push({ ...i, grupo: g.grupo });
      });
    });
    return m;
  }, [permisos]);

  /* ── QUIÉN VE EL INSTRUCTIVO DE ALTAS Y BAJAS ─────────────────────────────
   *
   * Nico (25/08/2026): «Que Erick y Ale puedan dar de alta un empleado o de
   * baja. (Decirles cómo)». Lo segundo es la mitad del pedido: la llave sin el
   * instructivo termina igual que antes, con el alta pedida por mensaje.
   *
   * Se muestra a quien PUEDE hacerlo. Explicarle a alguien cómo dar de alta un
   * empleado cuando el botón no le va a aparecer es mandarlo a buscar un lápiz
   * que no está, y después a preguntar por qué. `null` es «todavía no se sabe»
   * y muestra —misma regla que la lista de pantallas de acá arriba: quien
   * llega desde el mail de bienvenida está viendo el catálogo, no lo suyo. */
  const puedeAltasYBajas =
    permisos === null ||
    permisos.includes("cargar.gente") ||
    permisos.includes("cargar.rrhh");

  /* Los números de las secciones se cuentan solos porque una de ellas aparece
   * o no según quién mire. Escritos a mano, al que no ve el instructivo el
   * índice le saltaría de 3 a 5 — y un tutorial que se saltea un paso se lee
   * como que falta algo. Se llama en el orden en que se dibujan. */
  let seccion = 0;
  const n = () => (seccion += 1);

  const porGrupo = useMemo(() => {
    const g = {};
    mias.forEach((i) => (g[i.grupo] = g[i.grupo] || []).push(i));
    return g;
  }, [mias]);

  return (
    <div className="ayuda">
      <header className="ayuda-tapa">
        <h1>Cómo se usa {MARCA_LARGA}</h1>
        <p>
          {fullUser?.nombres ? `Hola ${fullUser.nombres}. ` : ""}
          Esto es todo lo que hace falta saber para empezar. Se lee en cinco
          minutos y queda acá para cuando haga falta.
        </p>
      </header>

      <section className="ayuda-bloque">
        <h2>{n()} · La primera vez</h2>
        <ol className="ayuda-pasos">
          <li>
            Entrás a <b>hub.fibot.ar</b> con <b>tu mail de la empresa</b> y la
            clave provisoria que te pasaron.
          </li>
          <li>
            El sistema te va a pedir que <b>elijas una clave tuya</b>. La
            provisoria deja de servir en ese momento.
          </li>
          <li>Listo. De ahí en más entrás con esa clave.</li>
        </ol>
        {/* «OLVIDÉ MI CONTRASEÑA» ESTÁ EN LA ENTRADA, y es lo que sirve a quien
            se la olvidó: decía «se cambia desde Tu contraseña», que es una
            pantalla de adentro — para llegar hay que haber entrado. */}
        <p className="ayuda-nota">
          Si te olvidás la clave, en la entrada tocá{" "}
          <b>Olvidé mi contraseña</b> y te llega un correo para elegir una
          nueva. Estando adentro, se cambia desde{" "}
          {fullUser ? (
            <Link to="/cambiar-password">Tu contraseña</Link>
          ) : (
            <b>Tu contraseña</b>
          )}
          . Si el correo no llega, pedí una provisoria nueva — no hay forma de
          recuperar la vieja, ni nosotros la vemos.
        </p>
        {/* El enlace a entrar va acá y no en el pie: quien llega sin sesión
            viene del mail de bienvenida y lo único que quiere es la puerta.
            Al que ya entró no se le ofrece, que sería mandarlo a loguearse de
            nuevo estando adentro. */}
        {!fullUser && (
          <p className="ayuda-nota">
            <Link to="/login">Entrar al hub →</Link>
          </p>
        )}
      </section>

      <section className="ayuda-bloque">
        <h2>{n()} · Cómo está armado</h2>
        <p>
          A la <b>izquierda</b> está el menú con todo lo que podés abrir.
          Arriba, el título dice dónde estás. El engranaje abre tus preferencias
          —tema claro u oscuro, color y fondo—, y la <b>✕</b> cierra la sesión.
        </p>
        <p className="ayuda-nota">
          En el teléfono el menú arranca plegado: se abre con el <b>☰</b> de
          arriba a la izquierda y se cierra solo al elegir una pantalla.
        </p>
      </section>

      {/* SIN SESIÓN NO SE LISTA NADA. Esta pantalla se abre desde el mail de
          la clave, sin haber entrado, y ahí mostraba el catálogo entero —los
          nombres de todas las pantallas del sistema— a cualquiera que tuviera
          el enlace. Igna (08/10): «sacá». A quien ya entró, sus pantallas por
          nombre y nada más: ni qué hace cada una ni qué hay que pedir. */}
      {fullUser && (
      <section className="ayuda-bloque">
        <h2>{n()} · Lo que vos podés abrir</h2>
        {Object.entries(porGrupo).map(([grupo, items]) => (
          <div key={grupo} className="ayuda-grupo">
            <h3>{grupo}</h3>
            <ul className="ayuda-lista">
              {items.map((i) => (
                <li key={i.id}>
                  <Icono nombre="lista" tam={16} />
                  <div>
                    <b>{i.label}</b>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
      )}

      {fullUser && puedeAltasYBajas && (
        <section className="ayuda-bloque">
          <h2>{n()} · Dar de alta y de baja a un empleado</h2>
          <p>
            Se hace desde <b>Padrones → Choferes y personal</b>, la misma
            pantalla para las dos cosas. Primero hay que apretar{" "}
            <b>Editar</b> arriba: mientras no lo apretás, la pantalla se mira y
            no se toca.
          </p>
          <h3>Alta</h3>
          <ol className="ayuda-pasos">
            <li>
              <b>Editar</b> → el botón <b>+ persona</b>.
            </li>
            <li>
              Completás <b>DNI</b>, <b>legajo</b>, nombre, empresa y puesto, y
              guardás.
            </li>
            <li>
              Queda firmada con tu nombre en la columna <b>Cargó</b>.
            </li>
          </ol>
          <h3>Baja</h3>
          <ol className="ayuda-pasos">
            <li>
              Buscás a la persona y con <b>Editar</b> prendido tocás su columna{" "}
              <b>Estado</b>.
            </li>
            <li>
              La ponés en <b>De baja</b>. Se guarda al salir de la celda.
            </li>
          </ol>
          <p className="ayuda-nota">
            <b>La baja no borra a nadie.</b> Se puede volver a poner en Activo
            el día que la persona vuelva.
          </p>
          <p className="ayuda-nota">
            La lista arranca filtrada en <b>Activo</b>, así que a alguien que
            diste de baja no lo vas a ver ahí: cambiá el filtro de{" "}
            <b>Estado</b> a <b>De baja</b> —o a todos— y aparece.
          </p>
          <p className="ayuda-nota">
            Si <b>Editar</b> no te aparece, pedilo.
          </p>
        </section>
      )}

      <section className="ayuda-bloque">
        <h2>{n()} · El bot de WhatsApp</h2>
        <p>
          Muchas de estas cosas también se preguntan por WhatsApp, sin abrir el
          hub. Lo más usado es <b>preguntar dónde está un camión</b>: se escribe
          el interno y contesta con la posición y el enlace al mapa.
        </p>
        {/* ── LO QUE CAMBIA SEGÚN QUIÉN SEA ─────────────────────────────
            Las guías del bot son varias (la general, portería, puerto,
            choferes, el asistente…) y acá va UNA: la que le toca a quien
            mira. Mostrarlas todas obligaría a cada uno a adivinar cuál es la
            suya, y elegir mal significa leer comandos que le rebotan. Todas
            juntas, recortadas por permiso, están en «¿Cómo se usa?». */}
        {guia?.tiene_whatsapp && (
          <p className="ayuda-guia-bot">
            {/* `.btn` es el botón del hub, el mismo de todos lados. Una clase
                propia acá sería un sexto botón para mantener. */}
            <a
              className="btn btn--primary"
              href={guia.url}
              target="_blank"
              rel="noreferrer"
            >
              {guia.titulo}
            </a>
            {guia.bajada && <span className="ayuda-nota">{guia.bajada}</span>}
          </p>
        )}

        {guia && !guia.tiene_whatsapp ? (
          /* SIN NÚMERO EN LA PLANILLA EL BOT NO CONTESTA NADA, así que acá no
             va un enlace a la guía general: sería enseñar a usar algo que a
             esta persona le va a rebotar. Va qué hacer, y nada de DÓNDE está
             cargado el número: cómo está armado por dentro no se cuenta. */
          <p className="ayuda-nota">
            <b>El bot todavía no te va a contestar.</b> Pedí que te carguen el
            número de WhatsApp y después mirá la guía desde acá.
          </p>
        ) : (
          <p className="ayuda-nota">Si el bot no te contesta, avisá.</p>
        )}
      </section>

      <section className="ayuda-bloque">
        <h2>{n()} · Si algo no anda</h2>
        <p>
          Avisá con <b>qué pantalla</b> y <b>qué esperabas ver</b>. Con eso
          alcanza — no hace falta sacar captura ni explicar el error.
        </p>
      </section>
    </div>
  );
};

export default Ayuda;
